// Phase 14e-a — /admin/shift-requests/$id manual-match page.
//
// Renders shift-request detail + 2 operator actions:
//   - Target a caregiver (opens distance-aware picker per 14e-a-8)
//   - Cancel by operator
//
// Reached via context navigation only (no list endpoint at v1 per
// 14e-a-7 watch item). Facility/Caregiver/Homecare detail shift
// sections link here per row + the dashboard audit-trail surfaces
// IDs as clickable links.

import { useMemo, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, CardHeader, Field, Input, useFocusTrap } from '@truecare/ui';
import { unwrap } from '@truecare/api-client';
import { actionErrorMessage } from '../lib/action-error';
import { api } from '../api';
import { AdminLayout } from '../components/AdminLayout';
import { adminBeforeLoad } from '../lib/admin-before-load';

export const Route = createFileRoute('/shift-requests/$id')({
  beforeLoad: ({ params }) => adminBeforeLoad(`/shift-requests/${params.id}`),
  component: ShiftRequestDetailPage,
});

interface ShiftRequestDetail {
  id: string;
  requestedByUserId: string;
  requestedByUserEmail: string;
  requestedByUserName: string;
  facilityId: string | null;
  facilityName: string | null;
  homecareClientId: string | null;
  homecareClientName: string | null;
  recipientId: string | null;
  recipientLabel: string | null;
  roleId: string;
  roleCode: string;
  setting: 'LTC' | 'HOMECARE';
  scheduledStart: string;
  scheduledEnd: string;
  locationLat: string | null;
  locationLng: string | null;
  specialConstraints: string | null;
  requiredSkills: string[];
  matchingState: string;
  offerTierSize: number;
  attemptCount: number;
  lastAttemptAt: string | null;
  currentTargetCaregiverId: string | null;
  currentTargetCaregiverName: string | null;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  cancelledAt: string | null;
  cancelReason: string | null;
}

// Server-provided eligible candidate (GET /admin/shift-requests/:id/candidate-caregivers).
interface CandidateItem {
  id: string;
  name: string;
  email: string;
  distanceKm: number | null;
  maxTravelKm: number;
  // Does their declared availability cover THIS shift's window? Annotation only —
  // the picker never filters on it (manual targeting is a deliberate override).
  availabilityMatchesShift: boolean;
  // Whether they've set ANY availability — lets us distinguish "set, not this slot"
  // (a real not-available signal) from "hasn't set any" (neutral).
  hasAvailabilityOnFile: boolean;
}

// Keep in exact sync with the backend TARGETABLE_STATES
// (apps/backend/src/shift/shift-lifecycle.service.ts, targetCaregivers).
const TARGETABLE_STATES = [
  'MATCHING',
  'REBROADCASTING_T2',
  'EXPIRED_POOL_EXHAUSTED',
  'EXPIRED_TIME_HIT',
];

function ShiftRequestDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['admin', 'shift-requests', id],
    queryFn: async () => {
       
      const r = (await unwrap(
        api.GET('/api/v1/admin/shift-requests/{id}' as never, {
          params: { path: { id } },
        } as never),
      )) as ShiftRequestDetail;
      return r;
    },
  });

  if (detailQuery.isPending) {
    return (
      <AdminLayout>
        <div className="space-y-4">
          <p className="text-sm text-ink-muted">Loading shift request…</p>
        </div>
      </AdminLayout>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <AdminLayout>
        <Alert tone="danger">
          Failed to load shift request. Verify the ID is correct + try again.
        </Alert>
      </AdminLayout>
    );
  }

  const detail = detailQuery.data;
  // Mirror the backend's TARGETABLE_STATES (ShiftLifecycleService.targetCaregivers):
  // targeting only makes sense while a request is still being matched or has
  // exhausted broadcast — otherwise the operator would hit a 409 on submit.
  const targetable = TARGETABLE_STATES.includes(detail.matchingState);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header>
          <h1 className="font-heading text-3xl text-forest md:text-4xl">
            Shift request #{detail.id.slice(0, 8)}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            Manual-match surface for operator intervention. Created{' '}
            {new Date(detail.createdAt).toLocaleString()}.
          </p>
        </header>

        <Card padding="lg">
          <CardHeader title="Request detail" />
          <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <DetailField label="Matching state" value={detail.matchingState} />
            <DetailField label="Role + setting" value={`${detail.roleCode} / ${detail.setting}`} />
            <DetailField
              label="Scheduled start"
              value={new Date(detail.scheduledStart).toLocaleString()}
            />
            <DetailField
              label="Scheduled end"
              value={new Date(detail.scheduledEnd).toLocaleString()}
            />
            <DetailField
              label="Buyer"
              value={
                detail.facilityName ??
                detail.homecareClientName ??
                detail.requestedByUserName
              }
            />
            <DetailField
              label="Service location"
              value={detail.recipientLabel ?? detail.facilityName ?? '—'}
            />
            <DetailField
              label="Location lat/lng"
              value={
                detail.locationLat && detail.locationLng
                  ? `${detail.locationLat}, ${detail.locationLng}`
                  : 'Not geocoded — distance matching disabled for this request'
              }
            />
            <DetailField
              label="Attempts"
              value={`${detail.attemptCount} offer${detail.attemptCount === 1 ? '' : 's'}${
                detail.lastAttemptAt
                  ? ` (last ${new Date(detail.lastAttemptAt).toLocaleString()})`
                  : ''
              }`}
            />
            <DetailField
              label="Current target"
              value={detail.currentTargetCaregiverName ?? '— (no active offer)'}
            />
            <DetailField
              label="Required skills"
              value={detail.requiredSkills.length > 0 ? detail.requiredSkills.join(', ') : '—'}
            />
            <DetailField
              label="Special constraints"
              value={detail.specialConstraints ?? '—'}
            />
            <DetailField
              label="Expires at"
              value={new Date(detail.expiresAt).toLocaleString()}
            />
          </dl>
        </Card>

        <Card padding="lg">
          <CardHeader title="Operator actions" />
          <div className="mt-4 flex flex-wrap gap-3">
            <Button
              variant="primary"
              onClick={() => setTargetModalOpen(true)}
              disabled={detail.cancelledAt !== null || !targetable}
              data-testid="open-target-modal"
            >
              Target a professional
            </Button>
            <Button
              variant="secondary"
              onClick={() => setCancelModalOpen(true)}
              disabled={detail.cancelledAt !== null}
              data-testid="open-cancel-modal"
            >
              Cancel by operator
            </Button>
          </div>
          {detail.cancelledAt ? (
            <Alert tone="warning" className="mt-4">
              Request cancelled at {new Date(detail.cancelledAt).toLocaleString()}.
              {detail.cancelReason && ` Reason: ${detail.cancelReason}`}
            </Alert>
          ) : (
            !targetable && (
              <Alert tone="info" className="mt-4">
                Targeting is only available while a request is still being matched
                (state: {detail.matchingState}). Already-filled or completed requests
                can't be targeted — void the current assignment first if you need to reassign.
              </Alert>
            )
          )}
        </Card>

        {targetModalOpen && (
          <TargetCaregiverModal
            shiftRequestId={detail.id}
            shiftLat={detail.locationLat}
            shiftLng={detail.locationLng}
            onClose={() => setTargetModalOpen(false)}
            onSuccess={() => {
              void qc.invalidateQueries({ queryKey: ['admin', 'shift-requests', id] });
              setTargetModalOpen(false);
            }}
          />
        )}

        {cancelModalOpen && (
          <CancelByOperatorModal
            shiftRequestId={detail.id}
            onClose={() => setCancelModalOpen(false)}
            onSuccess={() => {
              void qc.invalidateQueries({ queryKey: ['admin', 'shift-requests', id] });
              setCancelModalOpen(false);
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className="mt-1 text-ink">{value}</dd>
    </div>
  );
}

// ─── Caregiver picker modal ──────────────────────────────────────────────────

interface TargetCaregiverModalProps {
  shiftRequestId: string;
  shiftLat: string | null;
  shiftLng: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function TargetCaregiverModal({
  shiftRequestId,
  shiftLat,
  shiftLng,
  onClose,
  onSuccess,
}: TargetCaregiverModalProps) {
  const [query, setQuery] = useState('');
  // §5.2 — multi-select shortlist: push a tier-1 targeted offer to each; the
  // first to accept wins. Cap mirrors the backend schema (.max(10)).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');

  const MAX_SELECT = 10;
  const toggle = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_SELECT) next.add(id);
      return next;
    });

  // Eligible-only shortlist: the backend returns just the professionals who pass
  // all four hard gates for THIS request (active + role + setting + valid
  // credentials), already nearest-first — so the operator can never pick someone
  // they'd be rejected for (no_role / credential_invalid / setting), and there's
  // no client-side page cap silently dropping caregivers at scale.
  const caregiversQuery = useQuery({
    queryKey: ['admin', 'candidate-caregivers', shiftRequestId],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/shift-requests/{id}/candidate-caregivers' as never, {
          params: { path: { id: shiftRequestId } },
        } as never),
      )) as { items: CandidateItem[] },
  });

  const candidates = useMemo(() => {
    const items = caregiversQuery.data?.items ?? [];
    if (!query) return items;
    const q = query.toLowerCase();
    return items.filter(
      (c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q),
    );
  }, [caregiversQuery.data, query]);

  const mutation = useMutation({
    mutationFn: async (body: { caregiverIds: string[]; reason: string }) => {

      return unwrap(
        api.POST('/api/v1/admin/shifts/{requestId}/target-caregiver' as never, {
          params: { path: { requestId: shiftRequestId } },
          body,
        } as never),
      );
    },
    onSuccess,
  });

  const canSubmit = selectedIds.size >= 1 && reason.length >= 20 && !mutation.isPending;

  // audit medium — trap focus within the dialog + Escape-to-close via the shared
  // hook (replaces the manual keydown listener; also keeps Tab inside the modal).
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, { active: true, onEscape: onClose });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="target-caregiver-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      data-testid="target-caregiver-modal"
    >
      <div
        ref={panelRef}
        className="w-full max-w-3xl rounded-md border border-border bg-cream p-6 max-h-[90vh] overflow-y-auto"
      >
        <h2 id="target-caregiver-modal-title" className="font-heading text-xl text-forest">
          Target professional(s)
        </h2>
        <p className="mt-1 text-sm text-ink-muted">
          Select one or more professionals to push this shift to — the first to
          accept wins. Manual targeting bypasses the auto-matcher's geo +
          availability filters. The operator-override action is logged.
        </p>

        {!shiftLat || !shiftLng ? (
          <Alert tone="warning" className="mt-3">
            Shift location is not geocoded — distance display is disabled for this
            request. Sort + filter by name only.
          </Alert>
        ) : null}

        {mutation.isError && actionErrorMessage(mutation.error) && (
          <Alert tone="danger" className="mt-3">
            {actionErrorMessage(mutation.error)}
          </Alert>
        )}

        <div className="mt-4">
          <Field label="Search by name / email">
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter…"
              data-testid="picker-search"
            />
          </Field>
        </div>

        <div className="mt-4 max-h-80 overflow-y-auto border border-border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-ink/5 text-left">
              <tr>
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Distance</th>
                <th className="px-3 py-2">Availability</th>
              </tr>
            </thead>
            <tbody>
              {candidates.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center">
                    {caregiversQuery.isError ? (
                      // Don't disguise a failed fetch as "zero eligible" — say so + offer retry.
                      <span className="text-danger" data-testid="picker-load-error">
                        Couldn&apos;t load candidates.{' '}
                        <button
                          type="button"
                          className="underline"
                          onClick={() => void caregiversQuery.refetch()}
                        >
                          Retry
                        </button>
                      </span>
                    ) : (
                      <span className="text-ink-muted">
                        {caregiversQuery.isPending
                          ? 'Loading…'
                          : query
                            ? 'No eligible professionals match your search.'
                            : 'No eligible professionals for this shift (role / credentials / setting).'}
                      </span>
                    )}
                  </td>
                </tr>
              )}
              {candidates.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`picker-row-${c.id}`}
                  className={selectedIds.has(c.id) ? 'bg-forest/10' : 'hover:bg-ink/5'}
                  onClick={() => toggle(c.id)}
                >
                  <td className="px-3 py-2">
                    {/* The row's onClick is the single toggle path; the checkbox
                        is controlled read-only so a click on it bubbles to the
                        row and toggles exactly once (an onChange here would
                        double-fire with the row handler). */}
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      readOnly
                      aria-label={`Select ${c.name}`}
                      data-testid={`picker-checkbox-${c.id}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div>{c.name}</div>
                    <div className="text-xs text-ink-muted">{c.email}</div>
                  </td>
                  <td className="px-3 py-2">
                    {c.distanceKm === null ? (
                      <span className="text-ink-muted">—</span>
                    ) : c.distanceKm > c.maxTravelKm ? (
                      <span className="flex items-center gap-2">
                        <span className="text-warning">{c.distanceKm.toFixed(1)} km</span>
                        {/* Out-of-range badge: this professional's shift distance
                            exceeds their declared max radius. Still selectable —
                            manual targeting is a deliberate operator override — but
                            flagged so it's never accidental. */}
                        <span
                          className="rounded-full bg-warning/15 px-2 py-0.5 text-xs font-semibold text-warning"
                          title={`${c.distanceKm.toFixed(1)} km exceeds their ${c.maxTravelKm} km max travel radius`}
                          data-testid={`picker-out-of-range-${c.id}`}
                        >
                          Out of range · max {c.maxTravelKm} km
                        </span>
                      </span>
                    ) : (
                      <span>
                        {c.distanceKm.toFixed(1)} km{' '}
                        <span className="text-ink-muted">· within {c.maxTravelKm} km</span>
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {/* Availability annotation for THIS shift's window. Like distance,
                        it never filters — the operator lever targets anyone, confirming
                        availability out-of-band. Three states so "not available" isn't
                        confused with "hasn't set any availability yet". */}
                    {c.availabilityMatchesShift ? (
                      <span
                        className="rounded-full bg-success/15 px-2 py-0.5 text-xs font-semibold text-success"
                        title="Their declared availability covers this shift's window"
                        data-testid={`picker-available-${c.id}`}
                      >
                        Available
                      </span>
                    ) : c.hasAvailabilityOnFile ? (
                      <span
                        className="rounded-full bg-warning/15 px-2 py-0.5 text-xs font-semibold text-warning"
                        title="They've set availability, but none of it covers this shift's window"
                        data-testid={`picker-no-match-${c.id}`}
                      >
                        No matching availability
                      </span>
                    ) : (
                      <span
                        className="text-xs text-ink-muted"
                        title="This professional hasn't set any availability — confirm directly before targeting"
                        data-testid={`picker-no-availability-${c.id}`}
                      >
                        None set
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* §5-audit LOW — tell the operator when the shortlist cap is hit, so a
            no-op click on an 11th row reads as "cap reached", not a broken UI. */}
        {selectedIds.size >= MAX_SELECT && (
          <p className="mt-2 text-sm text-warning" role="status" data-testid="picker-cap-notice">
            Maximum {MAX_SELECT} professionals selected — deselect one to choose another.
          </p>
        )}

        <div className="mt-4">
          <Field label="Reason (≥20 characters — operator-override audit)">
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-md border border-border bg-cream p-2 text-sm"
              data-testid="picker-reason"
            />
          </Field>
        </div>

        <div className="mt-4 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={() => {
              if (selectedIds.size === 0) return;
              mutation.mutate({ caregiverIds: [...selectedIds], reason });
            }}
            data-testid="picker-submit"
          >
            {selectedIds.size > 1
              ? `Target ${selectedIds.size} professionals`
              : 'Target professional'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Cancel-by-operator modal ───────────────────────────────────────────────
//
// Phase 14e-a audit round 2 follow-up — the file header originally promised
// both Target + Cancel operator actions per 14e-a-7 lock, but the cancel
// surface was never wired. Backend endpoint POST /admin/shifts/:id/cancel-
// by-operator has been live since Phase 10f (commit 1ba9655). This modal
// closes the gap.
//
// This page is request-centric ($id IS the shift-request id), so it posts to
// the request-centric POST /admin/shift-requests/:id/cancel — which cancels the
// booked shift if one exists, else the pre-shift request itself. (Earlier this
// wrongly posted the request id to POST /admin/shifts/:id/cancel-by-operator,
// whose requireShift always 404'd it — "Shift not found".)

interface CancelByOperatorModalProps {
  shiftRequestId: string;
  onClose: () => void;
  onSuccess: () => void;
}

function CancelByOperatorModal({
  shiftRequestId,
  onClose,
  onSuccess,
}: CancelByOperatorModalProps) {
  const [reason, setReason] = useState('');
  const [requeueForMatching, setRequeueForMatching] = useState(false);

  const mutation = useMutation({
    mutationFn: async (body: { reason: string; requeueForMatching: boolean }) => {
       
      return unwrap(
        api.POST('/api/v1/admin/shift-requests/{id}/cancel' as never, {
          params: { path: { id: shiftRequestId } },
          body,
        } as never),
      );
    },
    onSuccess,
  });

  // audit medium — trap focus + Escape-to-close via the shared hook.
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, { active: true, onEscape: onClose });

  const canSubmit = reason.length >= 10 && !mutation.isPending;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-by-operator-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      data-testid="cancel-by-operator-modal"
    >
      <div ref={panelRef} className="w-full max-w-md rounded-md border border-border bg-cream p-6">
        <h2 id="cancel-by-operator-modal-title" className="font-heading text-xl text-forest">
          Cancel shift request
        </h2>
        <p className="mt-1 text-sm text-ink-muted">
          Operator-initiated cancellation. The action is logged. Per Phase 10f
          Workflows H/I/K, default behavior is NOT to re-broadcast (the
          invoice-correction path is the usual reason for cancelling). Check
          the box below only when you want the auto-matcher to retry.
        </p>

        {mutation.isError && actionErrorMessage(mutation.error) && (
          <Alert tone="danger" className="mt-3">
            {actionErrorMessage(mutation.error)}
          </Alert>
        )}

        <div className="mt-4">
          <Field label="Reason (≥10 characters)">
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-md border border-border bg-cream p-2 text-sm"
              data-testid="cancel-reason"
            />
          </Field>
        </div>

        <label className="mt-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={requeueForMatching}
            onChange={(e) => setRequeueForMatching(e.target.checked)}
            data-testid="cancel-requeue"
          />
          Re-broadcast to care professionals after cancellation
        </label>

        <div className="mt-4 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={() => mutation.mutate({ reason, requeueForMatching })}
            data-testid="cancel-submit"
          >
            Cancel shift
          </Button>
        </div>
      </div>
    </div>
  );
}
